/**
 * @file Handles request polling
 * */
import { fetchJson } from '@@/surface/api_fetch'
import type { FetchOptions } from '@padlet/fetch'
import { HTTPMethod } from '@padlet/fetch'

interface PollingOptions {
  intervalSecs: number
  maxAttempts: number
  method?: HTTPMethod
  fetchOptions?: FetchOptions
}

interface PollingArguments<R> {
  pollingUrl: string
  validationCallback?: (response: R) => boolean
  onIteration?: (response: R) => void
  options: PollingOptions
}

class PollTimeoutError extends Error {
  constructor(message: string = 'Polling timeout: max attempts reached') {
    super(message)
    this.name = 'PollTimeoutError'
  }
}

const defaultValidationCallback = (response): boolean => response.done

const poll = async <R = any>({
  pollingUrl,
  validationCallback,
  onIteration,
  options,
}: PollingArguments<R>): Promise<any> => {
  let attempts = 0
  const executePoll = async (resolve: (v: R) => void, reject: (e?: unknown) => void): Promise<void> => {
    const fetchOptions = options.fetchOptions ?? {}
    const response: R = await fetchJson(pollingUrl, {
      method: options.method ?? HTTPMethod.post,
      ...fetchOptions,
    })
    attempts++

    if (onIteration) {
      onIteration(response)
    }

    try {
      if (validationCallback != null ? validationCallback(response) : defaultValidationCallback(response)) {
        return resolve(response)
      } else if (attempts >= options.maxAttempts) {
        return reject(new PollTimeoutError())
      } else {
        setTimeout(executePoll, options.intervalSecs * 1000, resolve, reject)
      }
    } catch (validationError) {
      return reject(validationError)
    }
  }
  return await new Promise(executePoll)
}

export { poll, PollTimeoutError }
